import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
// Phase 7 sign-off: profile password.
import { MAX_LENGTHS } from '../utils/inputValidation';
import '../styles/authCard.css';

/**
 * Password strength scoring (4 levels) per spec:
 *  1 (weak)        : <8 chars OR only one of letters/digits
 *  2 (medium)      : 8+ with letters + digits
 *  3 (strong)      : 10+ with letters + digits + symbols
 *  4 (very strong) : 12+ with upper+lower + digits + symbols
 */
function scorePassword(p) {
  if (!p) return 0;
  const hasLower = /[a-z]/.test(p);
  const hasUpper = /[A-Z]/.test(p);
  const hasDigit = /\d/.test(p);
  const hasSymbol = /[^A-Za-z0-9]/.test(p);
  const hasLetter = hasLower || hasUpper;

  if (p.length < 8) return 1;
  if (!(hasLetter && hasDigit)) return 1;
  if (p.length >= 12 && hasLower && hasUpper && hasDigit && hasSymbol) return 4;
  if (p.length >= 10 && hasLetter && hasDigit && hasSymbol) return 3;
  return 2;
}

function strengthLabel(score, isKo) {
  if (score <= 0) return '';
  return [
    '',
    isKo ? '약함' : 'Weak',
    isKo ? '보통' : 'Medium',
    isKo ? '강함' : 'Strong',
    isKo ? '매우 강함' : 'Very strong',
  ][score];
}

export default function ResetPasswordPage() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { user, logout } = useAuth();

  const [verifyState, setVerifyState] = useState('checking'); // checking | valid | expired | invalid | used
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pw2Error, setPw2Error] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);

  // If a stale session exists when the user lands here from an email link,
  // sign them out so they cannot see the authenticated app behind this page.
  // The backend also invalidates server-side sessions on consume_token, but
  // we clear local storage immediately for instant UX correctness.
  useEffect(() => {
    if (user) {
      logout().catch(() => { /* clearing local state is enough */ });
    }
    // Run once on mount; logout reference is stable from context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verify token on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setVerifyState('invalid');
        return;
      }
      try {
        const res = await apiFetch(
          `/api/v1/auth/reset-password/verify?token=${encodeURIComponent(token)}`,
          { method: 'GET', skipAuth: true },
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (data?.valid) {
          setVerifyState('valid');
        } else {
          const err = data?.error || 'token_invalid';
          if (err === 'token_expired') setVerifyState('expired');
          else if (err === 'token_used') setVerifyState('used');
          else setVerifyState('invalid');
        }
      } catch {
        if (!cancelled) setVerifyState('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Success → 3s countdown → /login
  useEffect(() => {
    if (!success) return;
    if (countdown <= 0) {
      navigate('/login', { replace: true });
      return;
    }
    const id = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [success, countdown, navigate]);

  const score = scorePassword(pw);
  const matchOk = pw && pw2 && pw === pw2;
  const canSubmit = score >= 2 && matchOk && !submitting;

  const submit = async (e) => {
    if (e) e.preventDefault();
    setPwError('');
    setPw2Error('');
    if (score < 2) {
      setPwError(isKo
        ? '비밀번호는 8자 이상이며 문자와 숫자를 포함해야 합니다.'
        : 'Password must be at least 8 characters with letters and digits.');
      return;
    }
    if (!matchOk) {
      setPw2Error(isKo ? '비밀번호가 일치하지 않습니다.' : 'Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/v1/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password: pw }),
        skipAuth: true,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const err = data?.detail?.error || data?.error;
        if (err === 'token_expired') setVerifyState('expired');
        else if (err === 'token_used') setVerifyState('used');
        else if (err === 'token_invalid') setVerifyState('invalid');
        else if (err === 'password_too_weak') {
          setPwError(isKo
            ? '비밀번호 조건을 충족하지 않습니다.'
            : 'Password does not meet requirements.');
        } else {
          setPwError(isKo
            ? '일시적인 오류가 발생했습니다. 다시 시도해주세요.'
            : 'A temporary error occurred. Please try again.');
        }
        return;
      }
      setSuccess(true);
    } catch {
      setPwError(isKo
        ? '일시적인 오류가 발생했습니다. 다시 시도해주세요.'
        : 'A temporary error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Token error screen
  if (verifyState === 'expired' || verifyState === 'invalid' || verifyState === 'used') {
    const titleKo = verifyState === 'expired'
      ? '링크가 만료되었습니다'
      : verifyState === 'used'
      ? '이미 사용된 링크입니다'
      : '유효하지 않은 링크입니다';
    const titleEn = verifyState === 'expired'
      ? 'Link has expired'
      : verifyState === 'used'
      ? 'Link has already been used'
      : 'Invalid link';
    const descKo = verifyState === 'expired'
      ? '비밀번호 재설정 링크는 1시간 동안만 유효합니다.'
      : verifyState === 'used'
      ? '이 재설정 링크는 이미 사용되었습니다. 새로운 링크를 요청해주세요.'
      : '비밀번호 재설정 링크가 유효하지 않습니다.';
    const descEn = verifyState === 'expired'
      ? 'Password reset links are valid for 1 hour.'
      : verifyState === 'used'
      ? 'This reset link has already been used. Please request a new one.'
      : 'The password reset link is invalid.';

    return (
      <div className="vp-auth-page">
        <Link to="/" className="vp-auth-logo">VeriPass</Link>
        <div className="vp-auth-card">
          <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 12 }}>⏱</div>
          <h1 className="vp-auth-h1">{isKo ? titleKo : titleEn}</h1>
          <p className="vp-auth-sub">{isKo ? descKo : descEn}</p>
          <Link to="/auth/forgot-password" style={{ display: 'block', textDecoration: 'none' }}>
            <button type="button" className="vp-btn-primary">
              {isKo ? '다시 요청하기' : 'Request a new link'}
            </button>
          </Link>
          <hr className="vp-divider" />
          <div className="vp-card-footer">
            <Link to="/login" className="vp-btn-secondary">
              ← {isKo ? '로그인으로 돌아가기' : 'Back to sign in'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Loading
  if (verifyState === 'checking') {
    return (
      <div className="vp-auth-page">
        <Link to="/" className="vp-auth-logo">VeriPass</Link>
        <div className="vp-auth-card">
          <p className="vp-auth-sub">
            {isKo ? '링크 확인 중...' : 'Verifying link...'}
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="vp-auth-page">
        <Link to="/" className="vp-auth-logo">VeriPass</Link>
        <div className="vp-auth-card">
          <div className="vp-status success" style={{ marginTop: 0 }}>
            ✓ {isKo
              ? '비밀번호가 성공적으로 변경되었습니다.'
              : 'Your password was changed successfully.'}
          </div>
          <p className="vp-auth-sub" style={{ marginTop: 16 }}>
            {isKo
              ? `${countdown}초 후 로그인 페이지로 이동합니다.`
              : `Redirecting to sign in in ${countdown}s...`}
          </p>
          <Link to="/login" style={{ textDecoration: 'none' }}>
            <button type="button" className="vp-btn-primary">
              {isKo ? '로그인하러 가기' : 'Go to sign in'}
            </button>
          </Link>
        </div>
      </div>
    );
  }

  // Form (token valid)
  return (
    <div className="vp-auth-page">
      <Link to="/" className="vp-auth-logo">VeriPass</Link>

      <div className="vp-auth-card">
        <h1 className="vp-auth-h1">
          {isKo ? '새 비밀번호 설정' : 'Set a new password'}
        </h1>

        <form onSubmit={submit} noValidate style={{ marginTop: 8 }}>
          <div className="vp-form-row">
            <div className="vp-input-wrap">
              <input
                type={showPw ? 'text' : 'password'}
                className={`vp-input ${pwError ? 'is-error' : ''}`}
                maxLength={MAX_LENGTHS.password}
                placeholder={isKo ? '새 비밀번호 (8자 이상)' : 'New password (8+ chars)'}
                value={pw}
                onChange={(e) => { setPw(e.target.value); setPwError(''); }}
                disabled={submitting}
                autoFocus
                autoComplete="new-password"
              />
              <button
                type="button"
                className="vp-input-suffix"
                onClick={() => setShowPw(s => !s)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? '🙈' : '👁'}
              </button>
            </div>

            {pw && (
              <>
                <div className="vp-strength" aria-hidden="true">
                  {[1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      className={`vp-strength-seg ${i <= score ? `s${score}` : ''}`}
                    />
                  ))}
                </div>
                <div className={`vp-strength-label s${score}`}>
                  {strengthLabel(score, isKo)}
                </div>
              </>
            )}

            {pwError && <div className="vp-inline-error">{pwError}</div>}
          </div>

          <div className="vp-form-row">
            <input
              type={showPw ? 'text' : 'password'}
              className={`vp-input ${pw2Error ? 'is-error' : ''}`}
              maxLength={MAX_LENGTHS.password}
              placeholder={isKo ? '비밀번호 확인' : 'Confirm password'}
              value={pw2}
              onChange={(e) => { setPw2(e.target.value); setPw2Error(''); }}
              disabled={submitting}
              autoComplete="new-password"
            />
            {pw2 && pw && pw !== pw2 && (
              <div className="vp-inline-error">
                {isKo ? '비밀번호가 일치하지 않습니다.' : 'Passwords do not match.'}
              </div>
            )}
            {pw2Error && <div className="vp-inline-error">{pw2Error}</div>}
          </div>

          <button
            type="submit"
            className="vp-btn-primary"
            disabled={!canSubmit}
          >
            {submitting
              ? <><span className="vp-spinner" />{isKo ? '처리 중...' : 'Updating...'}</>
              : (isKo ? '비밀번호 변경하기' : 'Update password')}
          </button>
        </form>

        <hr className="vp-divider" />

        <div className="vp-card-footer">
          <Link to="/login" className="vp-btn-secondary">
            ← {isKo ? '로그인으로 돌아가기' : 'Back to sign in'}
          </Link>
        </div>
      </div>
    </div>
  );
}
